// Seller text at the top of the PDP (snippets/product-seller-text.liquid): long text collapses
// behind a "Visa mer" / "Visa mindre" toggle, like the app's listing preview.
//
// The snippet decides whether a block is collapsible when it renders (the rule below, repeated
// in Liquid so the text is clipped from first paint) and marks those roots with
// data-seller-text. This script shows the toggle only while the text is taller than the
// collapsed height, re-checks when fonts load and when the viewport changes, and switches
// between collapsed and expanded. Roots are marked data-initialized, so running it again (quick
// add re-injects the section's scripts) only picks up new roots.
(function () {
  if (window.MaiProductDetails) {
    window.MaiProductDetails.init(document);
    return;
  }

  // MaiApp COLLAPSE_AFTER_CHARS (src/lib/listItem/listingPreview.ts)
  var COLLAPSE_AFTER_CHARS = 160;
  var ROOT_SELECTOR = '[data-seller-text]';
  var COLLAPSED_HEIGHT_PROPERTY = '--product-seller-text-collapsed-height';
  var EXPANDED_CLASS = 'product-seller-text--expanded';
  // Keep in sync with the max-height transition in assets/component-pdp-details.css
  var ANIMATION_MS = 300;
  var OVERFLOWING_CLASS = 'product-seller-text--overflowing';

  // MaiApp isCollapsible: long text, or both the seller's comment and the defect description.
  function isCollapsible(comment, defectDescription) {
    var commentText = String(comment || '').trim();
    var defectText = String(defectDescription || '').trim();
    return commentText.length + defectText.length > COLLAPSE_AFTER_CHARS || (commentText !== '' && defectText !== '');
  }

  // The toggle is only useful when the text is cut off; 1px of slack for rounding.
  function isOverflowing(contentHeight, collapsedHeight) {
    return collapsedHeight > 0 && contentHeight > collapsedHeight + 1;
  }

  function toggleLabel(expanded, labels) {
    return expanded ? labels.labelLess : labels.labelMore;
  }

  function collapsedHeight(root) {
    return parseFloat(window.getComputedStyle(root).getPropertyValue(COLLAPSED_HEIGHT_PROPERTY)) || 0;
  }

  // scrollHeight is the full text height whether the body is clipped or not
  function evaluate(root) {
    var body = root.querySelector('[data-seller-text-body]');
    var toggle = root.querySelector('[data-seller-text-toggle]');
    if (!body || !toggle) return;
    var overflowing = isOverflowing(body.scrollHeight, collapsedHeight(root));
    root.classList.toggle(OVERFLOWING_CLASS, overflowing);
    toggle.hidden = !overflowing;
  }

  // Expanding and collapsing animate the clamp between the collapsed height and the full text
  // height; the fade over the clipped rows cross-fades with it (CSS). Without a body, or when
  // the caller asks for no animation, it is an instant class swap as before.
  function animateBody(root, body, expanded) {
    // No layout to animate (a stub, or a browser without these APIs): swap states instantly
    if (typeof body.getBoundingClientRect !== 'function' || !body.style) return;
    var from = body.getBoundingClientRect().height;
    var to = expanded ? body.scrollHeight : collapsedHeight(root);
    if (!to || Math.abs(to - from) < 1) return;
    body.style.overflow = 'hidden';
    body.style.maxHeight = from + 'px';
    body.offsetHeight; // reflow, so the height below animates
    body.style.transition = 'max-height ' + ANIMATION_MS + 'ms ease';
    body.style.maxHeight = to + 'px';
    window.setTimeout(function () {
      // Hand the clamp back to the stylesheet
      body.style.transition = '';
      body.style.maxHeight = '';
      body.style.overflow = '';
    }, ANIMATION_MS);
  }

  function setExpanded(root, expanded, animate) {
    var toggle = root.querySelector('[data-seller-text-toggle]');
    var body = root.querySelector('[data-seller-text-body]');
    if (animate && body) animateBody(root, body, expanded);
    root.classList.toggle(EXPANDED_CLASS, expanded);
    if (!toggle) return;
    toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    toggle.textContent = toggleLabel(expanded, toggle.dataset);
  }

  function initRoot(root) {
    if (root.hasAttribute('data-initialized')) return;
    root.setAttribute('data-initialized', '');
    var toggle = root.querySelector('[data-seller-text-toggle]');
    if (toggle) {
      toggle.addEventListener('click', function () {
        setExpanded(root, !root.classList.contains(EXPANDED_CLASS), true);
      });
    }
    // The clipped text and the fade over it expand on click too; the button stays the
    // accessible control, this is a bigger target for a pointer
    var body = root.querySelector('[data-seller-text-body]');
    if (body) {
      body.addEventListener('click', function () {
        if (!root.classList.contains(OVERFLOWING_CLASS)) return;
        if (root.classList.contains(EXPANDED_CLASS)) return;
        // Don't swallow a click that was only meant to select text
        var selection = window.getSelection && window.getSelection();
        if (selection && String(selection).length > 0) return;
        setExpanded(root, true, true);
      });
    }
    evaluate(root);
  }

  function init(scope) {
    (scope || document).querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  function refresh() {
    document.querySelectorAll(ROOT_SELECTOR + '[data-initialized]').forEach(evaluate);
  }

  var refreshScheduled = false;
  function scheduleRefresh() {
    if (refreshScheduled) return;
    refreshScheduled = true;
    window.requestAnimationFrame(function () {
      refreshScheduled = false;
      refresh();
    });
  }

  function start() {
    init(document);
    // Webfonts change line breaks and so the text height
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(refresh);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  window.addEventListener('resize', scheduleRefresh);
  window.addEventListener('orientationchange', scheduleRefresh);

  // Exposed for unit tests (tests/product-details.test.js); init also lets a re-run of this
  // script pick up new roots.
  window.MaiProductDetails = {
    COLLAPSE_AFTER_CHARS: COLLAPSE_AFTER_CHARS,
    isCollapsible: isCollapsible,
    isOverflowing: isOverflowing,
    toggleLabel: toggleLabel,
    setExpanded: setExpanded,
    init: init,
    refresh: refresh,
  };
})();
